/* eslint-disable sonarjs/no-duplicate-string */
import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  SimpleGrid,
  Stack,
  Text,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { FiCheck, FiShield } from "react-icons/fi";

interface FeatureItemProps {
  children: ReactNode;
  colorMode: "light" | "dark";
}

const FeatureItem = ({ children, colorMode }: FeatureItemProps) => {
  return (
    <HStack spacing={3} align="flex-start">
      <Flex
        align="center"
        justify="center"
        w={5}
        h={5}
        mt={0.5}
        borderRadius="full"
        bg="brand.500"
        flexShrink={0}
      >
        <Icon as={FiCheck} color="white" boxSize={3} aria-hidden="true" />
      </Flex>
      <Text
        color={
          colorMode === "light" ? "text.light-secondary" : "text.dark-muted"
        }
        fontSize="md"
        lineHeight="1.6"
      >
        {children}
      </Text>
    </HStack>
  );
};

const PricingFeatures = () => {
  const { colorMode } = useColorMode();

  return (
    <Stack
      spacing={8}
      p={{ base: 8, md: 12 }}
      flex={1}
      borderRight={{ lg: "1px solid" }}
      borderColor={colorMode === "light" ? "neutral.100" : "neutral.800"}
    >
      <VStack align="flex-start" spacing={4}>
        <Badge
          bg="brand.500"
          color="white"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing="0.1em"
        >
          Most Popular
        </Badge>
        <Heading
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight={800}
          color={
            colorMode === "light" ? "text.light-primary" : "text.dark-primary"
          }
        >
          Adiona Alert Premium
        </Heading>
        <Text
          color={
            colorMode === "light" ? "text.light-secondary" : "text.dark-muted"
          }
          fontSize="md"
          lineHeight="1.8"
        >
          Receive instant safety notifications on your satellite communicators
          anywhere in the U.S. and Canada. Plus, get crucial alerts for any
          stationary locations.
        </Text>
      </VStack>

      <Box>
        <HStack spacing={2} mb={6}>
          <Icon
            as={FiShield}
            color="brand.500"
            boxSize={5}
            aria-hidden="true"
          />
          <Text
            fontWeight={700}
            color={
              colorMode === "light" ? "text.light-primary" : "text.dark-primary"
            }
            textTransform="uppercase"
            fontSize="sm"
            letterSpacing="0.1em"
          >
            What&apos;s Included
          </Text>
        </HStack>

        <SimpleGrid columns={1} spacing={4}>
          <FeatureItem colorMode={colorMode}>
            Instant safety alerts on Garmin inReach<sup>1</sup>, ZOLEO
            <sup>2</sup>, & Bivy Stick<sup>3</sup> devices
          </FeatureItem>
          <FeatureItem colorMode={colorMode}>
            Instant safety alerts for stationary locations
          </FeatureItem>
          <FeatureItem colorMode={colorMode}>
            Coverage anywhere in the U.S. and Canada
          </FeatureItem>
          <FeatureItem colorMode={colorMode}>
            Seamless integration with your existing satellite devices
          </FeatureItem>
          <FeatureItem colorMode={colorMode}>
            Dedicated support whenever you need it
          </FeatureItem>
        </SimpleGrid>
      </Box>

      <Box pt={4}>
        <Text
          fontSize="xs"
          color={colorMode === "light" ? "text.light-muted" : "text.dark-muted"}
          lineHeight="1.8"
        >
          1. Garmin inReach users must have Location Tracking enabled.{" "}
          <Link color="brand.500" href="/help/in-reach-requirements">
            Learn more
          </Link>
          <br />
          2. ZOLEO users must have Location Share+ subscription.{" "}
          <Link color="brand.500" href="/help/zoleo-requirements">
            Learn more
          </Link>
          <br />
          3. Bivy Stick users must have an active plan.
        </Text>
      </Box>
    </Stack>
  );
};

export default PricingFeatures;
